import { z } from 'zod/v4';
import {
  EPriceType,
  IOnDemandCheckoutAbortResponse,
  IOnDemandCheckoutCompletionResponse,
  IOnDemandCheckoutProcessRequest,
  IOnDemandCheckoutProcessResponse,
  IOnDemandOrderData,
  IOnDemandOrderConfirmationRequest,
  IOnDemandPaymentTransaction,
  IStoredOnDemandOrderData,
} from './on-demand-shared.interfaces';
import { PaymentMethod } from '../payment-methods.interfaces';
import { EConfigurationChannel } from '../cms/configuration/configuration.enums';
import { OPERATOR_MAPPING_ID_SCHEMA } from '../cms/operator-mapping.interface';
import {
  ON_DEMAND_BOOKING_CONFIRMATION_REQUEST_SCHEMA,
  ON_DEMAND_BOOKING_RESPONSE_SCHEMA,
} from './on-demand-booking.validations';

const PAYMENT_METHOD_SCHEMA = z
  .object({ id: z.string().min(1) })
  .loose() as z.ZodType<PaymentMethod>;

const ON_DEMAND_ORDER_DATA_OBJECT_SCHEMA = z.object({
  bookingReference: z.string().min(1),
  systemRef: z.string().min(1),
  operatorUrl: z.url(),
  customerReference: z.uuid(),
  customer: z.object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.email(),
    phone: z.string().optional(),
  }),
  totalAmount: z.number().positive(),
  technicalCancellationReason: z.string().min(1),
  paymentMethod: PAYMENT_METHOD_SCHEMA.optional(),
  journey: z.object({
    date: z.string().optional(),
    origin: z.string().min(1),
    destination: z.string().min(1),
    departure: z.string().optional(),
    arrival: z.string().optional(),
    operator: z.string().optional(),
    changeableUntil: z.string().optional(),
    cancellableUntil: z.string().optional(),
  }),
  passengers: z
    .array(
      z.object({
        name: z.string().min(1),
        passengerType: z.string().min(1),
        existingPublicTransportTicket: z.string().optional(),
        extras: z.array(z.string()),
      })
    )
    .min(1),
  publicTransportTickets: z.array(z.string()),
  items: z
    .array(
      z.object({
        type: z.enum(EPriceType),
        product: z.string().min(1),
        passenger: z.string().min(1),
        origin: z.string().min(1),
        destination: z.string().min(1),
        vatRate: z.number().nonnegative(),
        grossPrice: z.number().nonnegative(),
      })
    )
    .min(1),
  labels: z.object({
    passenger: z.string().min(1),
    operator: z.string().min(1),
    departure: z.string().min(1),
    arrival: z.string().min(1),
    changeableUntil: z.string().min(1),
    cancellableUntil: z.string().min(1),
    notCancellable: z.string().min(1),
    existingPublicTransportTickets: z.string().min(1),
    extras: z.string().min(1),
  }),
});

export const ON_DEMAND_ORDER_DATA_SCHEMA: z.ZodType<IOnDemandOrderData> =
  ON_DEMAND_ORDER_DATA_OBJECT_SCHEMA;

export const STORED_ON_DEMAND_ORDER_DATA_SCHEMA: z.ZodType<IStoredOnDemandOrderData> =
  ON_DEMAND_ORDER_DATA_OBJECT_SCHEMA.extend({
    operatorMappingId: OPERATOR_MAPPING_ID_SCHEMA.optional(),
    appCode: z.string().min(1).optional(),
    checkoutExpiresAt: z.iso.datetime(),
    clientChannel: z.enum(EConfigurationChannel),
    guestCheckout: z.boolean(),
    operatorCancellationCompleted: z.boolean().optional(),
    paymentCompensated: z.boolean().optional(),
    cancellationEmailSent: z.boolean().optional(),
    cancellationReason: z.string().min(1).optional(),
  });

export const ON_DEMAND_ORDER_CONFIRMATION_REQUEST_SCHEMA: z.ZodType<IOnDemandOrderConfirmationRequest> =
  ON_DEMAND_ORDER_DATA_OBJECT_SCHEMA.extend({
    transactionId: z.uuid(),
  });

export const ON_DEMAND_CHECKOUT_REQUEST_SCHEMA = z.object({
  bookingReference: z.string().min(1),
  orderReference: z.uuid().optional(),
  totalAmount: z.number().positive(),
  sessionTimeout: z.number().int().positive(),
  paymentMethodId: z.string().min(1).optional(),
});

export const ON_DEMAND_CHECKOUT_PROCESS_REQUEST_SCHEMA: z.ZodType<IOnDemandCheckoutProcessRequest> =
  z.object({
    order: ON_DEMAND_ORDER_DATA_SCHEMA,
    booking: ON_DEMAND_BOOKING_CONFIRMATION_REQUEST_SCHEMA,
    payment: ON_DEMAND_CHECKOUT_REQUEST_SCHEMA,
  });

export const ON_DEMAND_CHECKOUT_PROCESS_RESPONSE_SCHEMA: z.ZodType<IOnDemandCheckoutProcessResponse> =
  z.object({
    orderId: z.uuid(),
    booking: ON_DEMAND_BOOKING_RESPONSE_SCHEMA,
    redirectUrl: z.url().optional(),
    completed: z.boolean(),
  });

export const ON_DEMAND_CHECKOUT_COMPLETION_RESPONSE_SCHEMA: z.ZodType<IOnDemandCheckoutCompletionResponse> =
  z.object({
    completed: z.boolean(),
  });

export const ON_DEMAND_CHECKOUT_ABORT_RESPONSE_SCHEMA: z.ZodType<IOnDemandCheckoutAbortResponse> =
  z.object({
    cancelled: z.boolean(),
  });

export const ON_DEMAND_PAYMENT_TRANSACTION_SCHEMA: z.ZodType<IOnDemandPaymentTransaction> =
  z.object({
    status: z.enum([
      'CHECKOUT',
      'AUTHORIZATION',
      'RESERVED',
      'CANCELED',
      'PURCHASED',
      'REJECTED',
      'REVERSE_PENDING',
      'REVERSED',
    ]),
    type: z.enum(['REFUND', 'RESERVATION', 'CREDIT', 'PURCHASE', 'CHARGE']),
    currency: z.literal('EUR'),
    value: z.number().nonnegative(),
    orderReference: z.string().optional(),
    purpose: z.string().min(1),
  });
